import {
  type AttachmentList,
  type CitationStatus,
  type CitationsData,
  ocrCitationStatus,
  type RenderedCitation,
} from "@esi/shared";
import { t } from "../i18n/i18n.ts";
import { downloadHref } from "../projects/download.ts";
import { wikiPath } from "../router.tsx";
import { attachmentDownloadHref } from "./attachments/api.ts";

/**
 * What a citation shows: the check's verdict, still checking, or a source gone from view:
 * deleted (the viewer could read it) or unavailable (deleted or narrowed, indistinguishably).
 */
export type CitationState = "pending" | "checking" | CitationStatus | "deleted" | "unavailable";

export function citationState(
  citation: RenderedCitation | undefined,
  data: CitationsData | null,
): CitationState {
  if (!citation || !data) {
    return "pending";
  }
  if (citation.title === null) {
    return citation.deleted ? "deleted" : "unavailable";
  }
  if (data.state === "checking") {
    return "checking";
  }
  // A flagged transcription is never shown as verified, whatever the check said.
  return ocrCitationStatus(citation.status, citation.provenance);
}

/** Where a chip stands: its text part and the raw offset of its citation. */
export interface ChipPosition {
  textPart: number;
  offset: number;
}

/**
 * A chip's state where it stands (#324): the check judged each statement's citations apart, so
 * a citation that failed in this statement reads unverified even when its source passed
 * elsewhere. A source unverified as a whole that did not fail here was not judged here:
 * unchecked. Without per-statement verdicts (older servers), or with no position (the sources
 * list), the source's own state.
 */
export function occurrenceState(
  state: CitationState,
  data: CitationsData | null,
  handle: string,
  at?: ChipPosition,
): CitationState {
  const verdicts = data?.state === "done" ? data.unverified : undefined;
  if (
    !at ||
    !verdicts ||
    (state !== "supported" && state !== "unsupported" && state !== "unchecked")
  ) {
    return state;
  }
  const failed = verdicts.some(
    (r) =>
      r.textPart === at.textPart &&
      r.start <= at.offset &&
      at.offset < r.end &&
      r.handles.includes(handle),
  );
  return failed ? "unsupported" : state === "unsupported" ? "unchecked" : state;
}

/** The state's words; a supported OCR passage is supported by the transcription only. */
export function stateText(state: CitationState, citation: RenderedCitation | undefined): string {
  if (state === "supported" && citation?.title !== null && citation?.provenance?.source === "ocr") {
    return t("citations.supportedByTranscription");
  }
  return t(`citations.state.${state}`);
}

/**
 * A cited wiki page opens its cited version in the wiki; any other source downloads. A file of
 * a compatible type opens instead (the preview's open target, openspec change open-in-browser).
 */
export function citationHref(
  projectId: string,
  citation: {
    documentId: string;
    version: number;
    kind?: "wiki_page" | undefined;
    attachment?: true | undefined;
  },
  conversationId?: string,
): string {
  return citation.attachment
    ? conversationId
      ? attachmentDownloadHref(conversationId, citation.documentId)
      : ""
    : citation.kind === "wiki_page"
      ? wikiPath(projectId, citation.documentId, citation.version)
      : downloadHref(projectId, citation.documentId);
}

/** A cached citation title is not permission. Only an authorized response can assert deletion. */
export function authorizedAttachmentCitation(
  citation: RenderedCitation,
  list: AttachmentList | undefined,
): RenderedCitation {
  if (citation.title === null || !citation.attachment) {
    return citation;
  }
  const file = list?.attachments.find((a) => a.id === citation.documentId);
  return file
    ? { ...citation, title: file.title }
    : { handle: citation.handle, status: citation.status, title: null };
}
